import {
  IonButton,
  IonContent,
  IonInput,
  IonLoading,
  IonModal
} from '@ionic/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  ButtonContainer,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { Icon, Text } from '@/components/memo';
import { SelectModal } from '@/components/modals';
import { useDentist } from '@/contexts';
import { useModal, useToast } from '@/hooks';
import {
  SPECIALIZATION_JOIN_SEPARATOR,
  SPECIALIZATION_OPTIONS
} from '@/utils/constants';
import { arrowBackOutline, checkmarkCircleOutline } from 'ionicons/icons';

import './UpdateSpecializations.css';

interface UpdateSpecializationsProps {
  isOpen: boolean;
  onClose: () => void;
  presentingElement?: HTMLElement | undefined;
}

const UpdateSpecializations: React.FC<UpdateSpecializationsProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const displayToast = useToast();
  const { selectedDentist: dentist, updateDentistMutation } = useDentist();

  const [specializations, setSpecializations] = useState<string[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);
  const [lastSavedSpecializations, setLastSavedSpecializations] = useState<
    string[]
  >([]);
  const {
    isOpen: isSelectModalOpen,
    onOpen: onOpenSelectModal,
    onClose: onCloseSelectModal
  } = useModal();

  const modal = useRef<HTMLIonModalElement | null>(null);

  useEffect(() => {
    if (dentist?.specialization) {
      const fetchedSpecializations = dentist.specialization.split(
        SPECIALIZATION_JOIN_SEPARATOR
      );
      setSpecializations(fetchedSpecializations);
      setLastSavedSpecializations(fetchedSpecializations);
    }
  }, [dentist?.specialization]);

  const copy = {
    subHeader: 'SPECIALIZATIONS',
    header: (
      <>
        Manage your <Text color="primary">specializations</Text>
      </>
    ),
    message: `Please select all of your specializations.`,
    buttonText: 'Update',
    inputLabel: 'Specializations',
    inputPlaceholder: 'Example: Orthodontics',
    modalTitle: (
      <>
        Select <Text color="primary">specializations</Text>
      </>
    )
  };

  const isSelected = (value: string) => specializations.includes(value);

  const isPreSelected = (value: string) =>
    lastSavedSpecializations.includes(value);

  const onSelect = (value: string, selected: boolean) => {
    setSpecializations((prevSpecializations) =>
      selected
        ? prevSpecializations
            .filter((specialization) => specialization !== value)
            .sort()
        : [...prevSpecializations, value].sort()
    );
  };

  const onCloseSelectModalWithSave = () => {
    onCloseSelectModal();
    setLastSavedSpecializations(specializations);
  };

  const onNext = useCallback(async () => {
    if (specializations.length > 0 && dentist?.id) {
      setHasError(false);
      await updateDentistMutation.mutateAsync({
        dentistId: dentist.id,
        dentist: {
          ...dentist,
          specialization: specializations.join(SPECIALIZATION_JOIN_SEPARATOR)
        }
      });
      displayToast({
        message: 'Dentist specializations updated successfully.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
      onClose();
    } else {
      setHasError(true);
      displayToast({
        message: 'Please select a valid specialization.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  }, [specializations, dentist, updateDentistMutation, onClose]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={presentingElement}
      ref={modal}
    >
      <IonContent>
        <Padding withSafeArea={!presentingElement}>
          <div className="update-specializations">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={onClose}
            />
            <Spacer top={16} bottom={24}>
              <h1 className="update-specializations__header">{copy.header}</h1>
              <p className="update-specializations__message">{copy.message}</p>
            </Spacer>

            <Spacer bottom={124}>
              <div className="update-specializations__form">
                <div
                  className={`update-specializations__input ${hasError ? 'update-specializations__input--error' : ''}`}
                >
                  <IonInput
                    id="specialization-input"
                    label={copy.inputLabel}
                    labelPlacement="stacked"
                    value={specializations.join(SPECIALIZATION_JOIN_SEPARATOR)}
                    placeholder={copy.inputPlaceholder}
                    onClick={() => {
                      setHasError(false);
                      onOpenSelectModal();
                    }}
                    readonly
                  />
                </div>
              </div>
            </Spacer>

            <ButtonContainer>
              <IonButton
                id="next-button"
                onClick={onNext}
                expand="block"
                fill="solid"
                size="default"
                color="primary"
                strong={true}
                disabled={updateDentistMutation.isPending}
              >
                <Icon slot="start" icon={checkmarkCircleOutline} />
                {copy.buttonText}
              </IonButton>
            </ButtonContainer>
          </div>
        </Padding>
        <IonLoading
          isOpen={updateDentistMutation.isPending}
          message="Loading..."
          spinner="crescent"
        />
        <SelectModal
          isOpen={isSelectModalOpen}
          // TODO: presentingElement is temporarily set to undefined due to an issue with Ionic modal card state when used with IonTabs.
          // Uncomment the line below and remove presentingElement={undefined} once the issue is resolved.
          // presentingElement={modal.current}
          presentingElement={undefined}
          title={copy.modalTitle}
          options={Object.values(SPECIALIZATION_OPTIONS)}
          isSelected={isSelected}
          isPreSelected={isPreSelected}
          onSelect={onSelect}
          onClose={onCloseSelectModalWithSave}
          multiple={true}
          showOthers={false}
        />
      </IonContent>
    </IonModal>
  );
};

export default UpdateSpecializations;
